import { useMemo } from "react";

import { LoopConfiguration, LoopType } from "../types";

import { usePromptTemplateInputVariables } from "@/queries/prompts/usePromptTemplateInputVariables";
import { useGetWorkflowIdByName } from "@/queries/workflows";
import useWorkflowVersion from "@/queries/workflows/useWorkflowVersion";

export const useLoopInputVariables = (configuration: LoopConfiguration | undefined) => {
  const loopType = configuration?.loop_type || LoopType.PROMPT;

  const promptConfig = configuration?.prompt_config;
  const {
    inputVariables: promptInputVariables,
    isLoading: promptIsLoading,
    error: promptError,
  } = usePromptTemplateInputVariables(promptConfig);

  const workflowConfig = configuration?.workflow_config;
  const workflowName = loopType === LoopType.WORKFLOW ? workflowConfig?.name : undefined;
  const hasWorkflowName = !!workflowName;

  const workflowIdQuery = useGetWorkflowIdByName(workflowName);

  const workflowVersion = useWorkflowVersion({
    workflowName: workflowName,
    label: workflowConfig?.workflow_label_name,
    number: workflowConfig?.workflow_version_number,
  });

  const inputVariables = useMemo(() => {
    if (loopType === LoopType.PROMPT) {
      return promptInputVariables;
    }
    // Only return input variables if workflow is properly configured
    if (!hasWorkflowName || !workflowVersion.data) {
      return [];
    }
    return Object.keys(workflowVersion.data?.required_input_variables ?? {});
  }, [loopType, promptInputVariables, workflowVersion.data, hasWorkflowName]);

  const isLoading =
    loopType === LoopType.PROMPT
      ? promptIsLoading
      : hasWorkflowName && (workflowIdQuery.isLoading || workflowVersion.isLoading);

  const error = useMemo(() => {
    if (loopType === LoopType.PROMPT) {
      return promptError;
    }
    if (!hasWorkflowName) {
      return undefined;
    }
    return workflowIdQuery.error || workflowVersion.error;
  }, [loopType, promptError, hasWorkflowName, workflowIdQuery.error, workflowVersion.error]);

  return {
    inputVariables,
    isLoading,
    error,
  };
};
